import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../Sidebar/AdminSidebar';
import AdminNavbar from '../Navbars/AdminNavbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { Oval } from 'react-loader-spinner';
import {
    AlertTriangle, Download, RefreshCw, Search, X, Layers,
    Truck, MapPin, Package, Clock,
} from 'lucide-react';

const formatCurrency = (amount: any) => {
    const num = Number(amount);
    return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDateTime = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-NG', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const statusChip = (status: string) => {
    switch (status) {
        case 'recovered':
            return 'bg-green-100 text-green-700 border-green-200';
        case 'partially_recovered':
            return 'bg-amber-100 text-amber-700 border-amber-200';
        default:
            return 'bg-red-100 text-red-700 border-red-200';
    }
};

const statusLabel = (status: string) => {
    switch (status) {
        case 'recovered': return 'Recovered';
        case 'partially_recovered': return 'Partial';
        default: return 'Pending';
    }
};

const groupTabs = [
    { key: 'run_manifest', label: 'Run Manifest', icon: Layers },
    { key: 'rider', label: 'Rider', icon: Truck },
    { key: 'fleet', label: 'Fleet', icon: Package },
    { key: 'state', label: 'Dest. State', icon: MapPin },
    { key: 'branch', label: 'Branch', icon: MapPin },
];

const emptyFilters = {
    search: '',
    rider_id: '',
    fleet_id: '',
    run_manifest_id: '',
    receiver_state: '',
    branch: '',
    status: 'all',
    is_shein: 'all',
    date_from: '',
    date_to: '',
    sort: 'latest',
};

function AutoAttemptedDeliveries() {
    const [rows, setRows] = useState<any>(null);
    const [stats, setStats] = useState<any>(null);
    const [grouped, setGrouped] = useState<any[]>([]);
    const [options, setOptions] = useState<any>({ riders: [], fleets: [], states: [], branches: [], manifests: [] });

    const [loader, setLoader] = useState(true);
    const [tableLoader, setTableLoader] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [filters, setFilters] = useState<any>(emptyFilters);
    const [searchInput, setSearchInput] = useState('');
    const [groupBy, setGroupBy] = useState('run_manifest');
    const [showGrouped, setShowGrouped] = useState(true);

    const fetchAll = useCallback(async (page = 1, fullLoader = false) => {
        if (fullLoader) setLoader(true);
        else setTableLoader(true);

        try {
            const params = { ...filters, page, per_page: 20 };

            const [listRes, statsRes, groupRes] = await Promise.all([
                SuperAdminApis.getAutoAttemptedDeliveries(params),
                SuperAdminApis.getAutoAttemptedStats(params),
                SuperAdminApis.getAutoAttemptedGrouped({ ...params, group_by: groupBy }),
            ]);

            if (listRes?.status) setRows(listRes.data);
            if (statsRes?.status) setStats(statsRes.data);
            if (groupRes?.status) setGrouped(groupRes.data || []);
        } catch (error) {
            console.error('Error fetching auto-attempted deliveries:', error);
            toast.error('Failed to load auto-attempted deliveries');
        } finally {
            setLoader(false);
            setTableLoader(false);
        }
    }, [filters, groupBy]);

    useEffect(() => {
        (async () => {
            try {
                const res = await SuperAdminApis.getAutoAttemptedFilterOptions();
                if (res?.status) setOptions(res.data);
            } catch {
                // Filter dropdowns are a convenience — the page still works without them.
            }
        })();
        fetchAll(1, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Refetch whenever a filter or the grouping axis changes.
    useEffect(() => {
        fetchAll(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, groupBy]);

    const setFilter = (key: string, value: string) => {
        setFilters((prev: any) => ({ ...prev, [key]: value }));
    };

    const handleSearch = () => setFilter('search', searchInput);

    const handleClearFilters = () => {
        setSearchInput('');
        setFilters(emptyFilters);
    };

    const activeFilterCount = Object.keys(emptyFilters).filter(
        (k) => filters[k] !== (emptyFilters as any)[k]
    ).length;

    const handleExport = async () => {
        try {
            setExporting(true);
            const blob = await SuperAdminApis.exportAutoAttemptedDeliveries(filters);
            if (!blob) {
                toast.error('Export failed.');
                return;
            }
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `auto-attempted-deliveries-${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Export downloaded.');
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export.');
        } finally {
            setExporting(false);
        }
    };

    const paginator = (page: number) => fetchAll(page);

    const pageLinks = rows?.links || [];

    return (
        <>
            <AdminSidebar title="Auto-Attempted Deliveries" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Auto-Attempted Deliveries" />
                <ToastContainer />
                <div className="lg:px-10 pb-10 px-6 bg-[#FAFAFA] min-h-screen">

                    {/* Header */}
                    <div className="pt-6 pb-4 flex flex-wrap gap-3 justify-between items-start">
                        <div>
                            <h1 className="text-[20px] font-[700] text-[#333333] flex items-center gap-2">
                                <AlertTriangle className="w-5 h-5 text-orange-500" />
                                Auto-Attempted Deliveries
                            </h1>
                            <p className="text-[13px] text-[#808080] mt-1 max-w-3xl">
                                Shipments the system automatically posted to <strong>Attempted Delivery</strong> because they were
                                still Out for Delivery as the 24-hour partner deadline approached. Each one carries a fine that is
                                recovered from the rider or fleet's next approved delivery payment. Treat these shipments so they
                                get back out for delivery.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => fetchAll(1, true)}
                                className="flex items-center gap-1.5 bg-white border border-gray-300 text-[13px] px-3 py-2 rounded-md hover:bg-gray-50"
                            >
                                <RefreshCw className="w-4 h-4" /> Refresh
                            </button>
                            <button
                                onClick={handleExport}
                                disabled={exporting}
                                className="flex items-center gap-1.5 bg-[#E04343] text-white text-[13px] px-3 py-2 rounded-md hover:bg-[#c93a3a] disabled:opacity-50"
                            >
                                <Download className="w-4 h-4" /> {exporting ? 'Exporting…' : 'Export CSV'}
                            </button>
                        </div>
                    </div>

                    {loader ? (
                        <div className="flex justify-center py-20">
                            <Oval visible height="50" width="50" color="#E04343" secondaryColor="#FCBF94" ariaLabel="oval-loading" />
                        </div>
                    ) : (
                        <>
                            {/* Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                                <div className="bg-white rounded-[10px] p-3 text-center shadow-sm border">
                                    <p className="text-[22px] font-[700] text-[#333333]">{stats?.total_shipments ?? 0}</p>
                                    <p className="text-[11px] text-[#808080] mt-0.5">Shipments</p>
                                </div>
                                <div className="bg-white rounded-[10px] p-3 text-center shadow-sm border">
                                    <p className="text-[22px] font-[700] text-red-600">{formatCurrency(stats?.total_fined)}</p>
                                    <p className="text-[11px] text-[#808080] mt-0.5">Total Fined</p>
                                </div>
                                <div className="bg-white rounded-[10px] p-3 text-center shadow-sm border">
                                    <p className="text-[22px] font-[700] text-green-600">{formatCurrency(stats?.total_recovered)}</p>
                                    <p className="text-[11px] text-[#808080] mt-0.5">Recovered</p>
                                </div>
                                <div className="bg-white rounded-[10px] p-3 text-center shadow-sm border">
                                    <p className="text-[22px] font-[700] text-amber-600">{formatCurrency(stats?.total_outstanding)}</p>
                                    <p className="text-[11px] text-[#808080] mt-0.5">Outstanding</p>
                                </div>
                                <div className="bg-white rounded-[10px] p-3 text-center shadow-sm border">
                                    <p className="text-[22px] font-[700] text-[#333333]">{stats?.riders_affected ?? 0}</p>
                                    <p className="text-[11px] text-[#808080] mt-0.5">Riders Affected</p>
                                </div>
                                <div className="bg-white rounded-[10px] p-3 text-center shadow-sm border">
                                    <p className="text-[22px] font-[700] text-[#333333]">{stats?.manifests_affected ?? 0}</p>
                                    <p className="text-[11px] text-[#808080] mt-0.5">Manifests</p>
                                </div>
                            </div>

                            {/* Unattributed warning — these fines can never be recovered, so
                                they are called out rather than left to inflate "outstanding". */}
                            {stats?.unattributed > 0 && (
                                <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-[10px] text-[12px] text-amber-800">
                                    <strong>{stats.unattributed}</strong> fine{stats.unattributed === 1 ? '' : 's'} could not be
                                    linked to a rider wallet (no run manifest, or the rider has no active wallet). These are
                                    included in the totals above but cannot be recovered automatically.
                                </div>
                            )}

                            {/* Filters */}
                            <div className="bg-white rounded-[10px] p-4 shadow-sm border mb-5">
                                <div className="flex flex-wrap gap-3 items-end">
                                    <div className="flex-1 min-w-[220px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">Search</label>
                                        <div className="flex">
                                            <input
                                                value={searchInput}
                                                onChange={(e) => setSearchInput(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                                placeholder="Shipment no, manifest, rider, city…"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-l-md text-[13px] focus:ring-1 focus:ring-[#E04343] focus:border-[#E04343]"
                                            />
                                            <button onClick={handleSearch} className="px-3 bg-[#E04343] text-white rounded-r-md">
                                                <Search className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="min-w-[150px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">Rider</label>
                                        <select value={filters.rider_id} onChange={(e) => setFilter('rider_id', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md text-[13px]">
                                            <option value="">All riders</option>
                                            {options.riders?.map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
                                        </select>
                                    </div>

                                    <div className="min-w-[150px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">Fleet</label>
                                        <select value={filters.fleet_id} onChange={(e) => setFilter('fleet_id', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md text-[13px]">
                                            <option value="">All fleets</option>
                                            {options.fleets?.map((f: any) => <option key={f.id} value={f.id}>{f.name}</option>)}
                                        </select>
                                    </div>

                                    <div className="min-w-[140px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">Dest. State</label>
                                        <select value={filters.receiver_state} onChange={(e) => setFilter('receiver_state', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md text-[13px]">
                                            <option value="">All states</option>
                                            {options.states?.map((s: string) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>

                                    <div className="min-w-[140px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">Branch</label>
                                        <select value={filters.branch} onChange={(e) => setFilter('branch', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md text-[13px]">
                                            <option value="">All branches</option>
                                            {options.branches?.map((b: string) => <option key={b} value={b}>{b}</option>)}
                                        </select>
                                    </div>

                                    <div className="min-w-[130px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">Fine Status</label>
                                        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md text-[13px]">
                                            <option value="all">All</option>
                                            <option value="outstanding">Outstanding</option>
                                            <option value="pending">Pending</option>
                                            <option value="partially_recovered">Partially recovered</option>
                                            <option value="recovered">Recovered</option>
                                        </select>
                                    </div>

                                    <div className="min-w-[110px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">Shein</label>
                                        <select value={filters.is_shein} onChange={(e) => setFilter('is_shein', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md text-[13px]">
                                            <option value="all">All</option>
                                            <option value="true">Shein only</option>
                                            <option value="false">Non-Shein</option>
                                        </select>
                                    </div>

                                    <div className="min-w-[140px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">From</label>
                                        <input type="date" value={filters.date_from} onChange={(e) => setFilter('date_from', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md text-[13px]" />
                                    </div>

                                    <div className="min-w-[140px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">To</label>
                                        <input type="date" value={filters.date_to} onChange={(e) => setFilter('date_to', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md text-[13px]" />
                                    </div>

                                    <div className="min-w-[130px]">
                                        <label className="block text-[11px] font-[600] text-[#808080] mb-1">Sort</label>
                                        <select value={filters.sort} onChange={(e) => setFilter('sort', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-md text-[13px]">
                                            <option value="latest">Newest first</option>
                                            <option value="oldest">Oldest first</option>
                                            <option value="highest_fine">Highest fine</option>
                                            <option value="manifest">By manifest</option>
                                        </select>
                                    </div>

                                    {activeFilterCount > 0 && (
                                        <button onClick={handleClearFilters}
                                            className="flex items-center gap-1 text-[12px] text-[#E04343] px-3 py-2 hover:underline">
                                            <X className="w-3.5 h-3.5" /> Clear ({activeFilterCount})
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Grouped summary */}
                            <div className="bg-white rounded-[10px] shadow-sm border mb-5">
                                <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-[14px] font-[700] text-[#333333]">Grouped Summary</h2>
                                        <button onClick={() => setShowGrouped((s) => !s)}
                                            className="text-[12px] text-[#808080] hover:underline">
                                            {showGrouped ? 'Hide' : 'Show'}
                                        </button>
                                    </div>
                                    <div className="flex gap-1 bg-gray-100 rounded-md p-1">
                                        {groupTabs.map((t) => {
                                            const Icon = t.icon;
                                            return (
                                                <button key={t.key} onClick={() => setGroupBy(t.key)}
                                                    className={`flex items-center gap-1 px-2.5 py-1 rounded text-[12px] font-[600] transition ${groupBy === t.key ? 'bg-[#E04343] text-white' : 'text-gray-600 hover:bg-gray-200'}`}>
                                                    <Icon className="w-3.5 h-3.5" /> {t.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {showGrouped && (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-[13px]">
                                            <thead className="bg-[#FAFAFA] text-[#808080] text-[11px] uppercase">
                                                <tr>
                                                    <th className="text-left px-4 py-2.5 font-[600]">Group</th>
                                                    <th className="text-right px-4 py-2.5 font-[600]">Shipments</th>
                                                    <th className="text-right px-4 py-2.5 font-[600]">Fined</th>
                                                    <th className="text-right px-4 py-2.5 font-[600]">Recovered</th>
                                                    <th className="text-right px-4 py-2.5 font-[600]">Outstanding</th>
                                                    <th className="text-left px-4 py-2.5 font-[600]">Last Attempt</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {grouped.length === 0 ? (
                                                    <tr><td colSpan={6} className="text-center py-6 text-[#808080]">No data for this grouping.</td></tr>
                                                ) : grouped.map((g, i) => (
                                                    <tr key={`${g.group_key}-${i}`} className="border-t hover:bg-[#FAFAFA]">
                                                        <td className="px-4 py-2.5 font-[600] text-[#333333]">{g.group_label}</td>
                                                        <td className="px-4 py-2.5 text-right">{g.shipments}</td>
                                                        <td className="px-4 py-2.5 text-right text-red-600">{formatCurrency(g.total_fined)}</td>
                                                        <td className="px-4 py-2.5 text-right text-green-600">{formatCurrency(g.total_recovered)}</td>
                                                        <td className="px-4 py-2.5 text-right text-amber-600 font-[600]">{formatCurrency(g.outstanding)}</td>
                                                        <td className="px-4 py-2.5 text-[#808080]">{formatDateTime(g.last_attempt)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            {/* Detail table */}
                            <div className="bg-white rounded-[10px] shadow-sm border">
                                <div className="p-4 border-b flex items-center justify-between">
                                    <h2 className="text-[14px] font-[700] text-[#333333]">
                                        Shipments {rows?.total !== undefined && <span className="text-[#808080] font-[400]">({rows.total})</span>}
                                    </h2>
                                    {tableLoader && <Oval visible height="18" width="18" color="#E04343" secondaryColor="#FCBF94" ariaLabel="loading" />}
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-[13px]">
                                        <thead className="bg-[#FAFAFA] text-[#808080] text-[11px] uppercase">
                                            <tr>
                                                <th className="text-left px-4 py-2.5 font-[600]">Shipment</th>
                                                <th className="text-left px-4 py-2.5 font-[600]">Run Manifest</th>
                                                <th className="text-left px-4 py-2.5 font-[600]">Rider</th>
                                                <th className="text-left px-4 py-2.5 font-[600]">Fleet</th>
                                                <th className="text-left px-4 py-2.5 font-[600]">Destination</th>
                                                <th className="text-left px-4 py-2.5 font-[600]">Branch</th>
                                                <th className="text-left px-4 py-2.5 font-[600]">OFD → Auto-Attempt</th>
                                                <th className="text-left px-4 py-2.5 font-[600]">Tracking Remark</th>
                                                <th className="text-right px-4 py-2.5 font-[600]">Fine</th>
                                                <th className="text-center px-4 py-2.5 font-[600]">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!rows?.data?.length ? (
                                                <tr>
                                                    <td colSpan={10} className="text-center py-10 text-[#808080]">
                                                        No auto-attempted deliveries for the selected filters.
                                                    </td>
                                                </tr>
                                            ) : rows.data.map((r: any) => (
                                                <tr key={r.id} className="border-t hover:bg-[#FAFAFA]">
                                                    <td className="px-4 py-3">
                                                        <span className="font-[600] text-[#333333]">{r.shipment_number}</span>
                                                        {r.is_shein && (
                                                            <span className="ml-1.5 text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">SHEIN</span>
                                                        )}
                                                        <div className="text-[11px] text-[#808080]">{r.sender_name || '—'}</div>
                                                    </td>
                                                    <td className="px-4 py-3">{r.run_manifest_name || '—'}</td>
                                                    <td className="px-4 py-3">{r.rider_name || <span className="text-amber-600">Unattributed</span>}</td>
                                                    <td className="px-4 py-3">{r.fleet_name || '—'}</td>
                                                    <td className="px-4 py-3">
                                                        <div>{r.receiver_state || '—'}</div>
                                                        <div className="text-[11px] text-[#808080]">{r.receiver_city || ''}</div>
                                                    </td>
                                                    <td className="px-4 py-3">{r.branch || '—'}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="text-[11px] text-[#808080]">{formatDateTime(r.ofd_at)}</div>
                                                        <div className="text-[11px] text-[#333333] flex items-center gap-1">
                                                            <Clock className="w-3 h-3" /> {formatDateTime(r.auto_attempted_at)}
                                                        </div>
                                                        <div className="text-[10px] text-orange-600">{Number(r.hours_elapsed).toFixed(1)}h elapsed</div>
                                                    </td>
                                                    <td className="px-4 py-3 text-[11px] text-[#808080] max-w-[200px]">{r.ffdx_remark || '—'}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <div className="font-[600] text-red-600">{formatCurrency(r.fine_amount)}</div>
                                                        {Number(r.amount_recovered) > 0 && (
                                                            <div className="text-[11px] text-green-600">{formatCurrency(r.amount_recovered)} rec.</div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-center">
                                                        <span className={`text-[11px] px-2 py-1 rounded border ${statusChip(r.status)}`}>
                                                            {statusLabel(r.status)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {rows?.last_page > 1 && (
                                    <div className="flex flex-wrap gap-1 justify-center p-4 border-t">
                                        {pageLinks.map((link: any, i: number) => (
                                            <button
                                                key={i}
                                                disabled={!link.url}
                                                onClick={() => {
                                                    if (!link.url) return;
                                                    const page = new URL(link.url).searchParams.get('page');
                                                    if (page) paginator(Number(page));
                                                }}
                                                className={`px-3 py-1.5 text-[12px] rounded border ${link.active
                                                    ? 'bg-[#E04343] text-white border-[#E04343]'
                                                    : link.url
                                                        ? 'bg-white text-[#333333] border-gray-300 hover:bg-gray-50'
                                                        : 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed'
                                                    }`}
                                                dangerouslySetInnerHTML={{ __html: link.label }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default AutoAttemptedDeliveries;
